'use client';

import { ReactNode, useEffect, useState } from 'react';

/**
 * Defers rendering children until after client hydration.
 * Use for interactive components that depend on layout/DOM measurements (marquee, carousel,
 * MUI Tabs, etc.) when pages are statically served (e.g. S3/CloudFront).
 *
 * We mount immediately after hydration (no requestAnimationFrame). When HTML is served from
 * S3/CloudFront, the browser may not run rAF until the first user interaction, which made
 * interactive content (e.g. marquee) appear only after the first click. Mounting in useEffect
 * ensures the JS version shows as soon as the bundle has loaded and hydrated.
 * If you see layout glitches (wrong measurements on first paint), you can try wrapping
 * setReady(true) in requestAnimationFrame — at the cost of the "first click" delay again.
 */
export function ClientOnly({
  children,
  fallback = null,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(true);
  }, []);

  if (!ready) return <>{fallback}</>;
  return <>{children}</>;
}
